import classnames from 'classnames';
import { useField } from 'formik';
import { cloneDeep, forEach, set } from 'lodash';
import React, { useCallback, useMemo } from 'react';
import validate from 'validate.js';
import EntityFieldContent from './EntityFieldContent';
import EntityFieldEdit from './EntityFieldEdit';
import EntityFieldView from './EntityFieldView';
import useEntityFormContext from './useEntityFormContext';
const EntityField = ({ name, columns, required = false, editable = true, normalize = v => v, multipleValues, autoFilledValue, validate: validateExternal, constraints: constraintsExternal, ...restProps }) => {
    const { columns: contextColumns, fieldWrapperClassNames, isEditing, isSubmitting, entityName, submitCount, isValid, creating, values, setValues, setTouched, touched: touchedFields, validateOnMount, } = useEntityFormContext();
    const validateField = useCallback((value) => {
        if (validateExternal) {
            const error = validateExternal(value);
            if (error) {
                return error;
            }
        }
        const _required = typeof required === 'function' ? required(value) : required;
        if (!_required && !constraintsExternal) {
            return undefined;
        }
        const constraints = _required
            ? {
                ...constraintsExternal,
                presence: {
                    allowEmpty: false,
                    message: '^Required',
                },
            }
            : { ...constraintsExternal };
        validate.validators.custom = (value, options) => (validate.isFunction(options) ? options() : null);
        const validateMethod = multipleValues ? validate : validate.single;
        const result = validateMethod(value, constraints, {
            format: 'flat',
        });
        return result ? result.join(', ') : result;
    }, [validateExternal, constraintsExternal, multipleValues, required]);
    const [field, meta, helpers] = useField({
        name,
        validate: validateField,
    });
    const fieldProps = useMemo(() => {
        const { name, value, onBlur, onChange } = field;
        const { touched: fieldTouched, initialValue, error: fieldError } = meta;
        const { setValue } = helpers;
        const newTouched = { ...touchedFields };
        set(newTouched, name, true);
        //TODO workaround for the case when useField runs validation with 'previous' value
        let error = !isValid ? validateField(value) : '';
        let dirty = error === undefined && value !== initialValue;
        let touched = fieldTouched;
        if (validateOnMount && fieldError) {
            error = fieldError;
            dirty = true;
            touched = true;
        }
        return {
            input: {
                name,
                value,
                onBlur,
                onChange: val => {
                    if (val === null || val === void 0 ? void 0 : val.target) {
                        val.target.value = normalize(val.target.value);
                        onChange(val);
                    }
                    else if (val &&
                        typeof val === 'object' &&
                        val.hasOwnProperty(name)) {
                        // used for composed fields like from+to
                        // pay attention to use with trees. when you have "parent" both in entity and in selected tree node
                        // pay attention to use with any other object selectors with the same keys
                        const newValues = cloneDeep(values);
                        forEach(val, (v, k) => set(newValues, k, v));
                        setValues(newValues);
                    }
                    else {
                        setValue(normalize(val));
                    }
                    val && setTouched(newTouched, false);
                },
            },
            meta: {
                debug: {
                    isValid,
                    value,
                    initialValue,
                    fieldError,
                    validateOnMount,
                },
                dirty,
                error,
                form: entityName,
                submitFailed: !!submitCount && !isValid && !isSubmitting,
                submitting: isSubmitting,
                touched,
                wrapperClassname: classnames(fieldWrapperClassNames),
            },
        };
    }, [
        validateOnMount,
        validateField,
        touchedFields,
        setTouched,
        setValues,
        values,
        normalize,
        isValid,
        field,
        meta,
        entityName,
        isSubmitting,
        fieldWrapperClassNames,
        submitCount,
        helpers,
    ]);
    return (<EntityFieldContent columns={columns !== null && columns !== undefined ? columns : contextColumns} component={editable && (isEditing || creating)
        ? EntityFieldEdit
        : EntityFieldView} {...restProps} autoFilledValue={autoFilledValue} multipleValues={multipleValues} name={name} required={required} validate={validateField} {...fieldProps}/>);
};
export default EntityField;
